import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient } from '@/lib/axios';
import type { User } from '@/types/user';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

interface JwtPayload {
  sub: string;
  email: string;
  username: string;
  iat: number;
  exp: number;
}

/** Decode a JWT without verifying the signature (client-side use only). */
function decodeJwt(token: string): JwtPayload | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function userFromJwt(payload: JwtPayload): User {
  return { id: payload.sub, email: payload.email, username: payload.username };
}

/**
 * Turn a login failure into copy a user can act on. Never surfaces axios's raw
 * "Request failed with status code 401" text — that's a wire-protocol detail,
 * not something a person trying to sign in should have to interpret.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { message?: string } } })
      .response;
    const backendMessage = response?.data?.message;
    if (backendMessage) return backendMessage;

    if (response?.status === 401 || response?.status === 403) {
      return 'Incorrect email/username or password.';
    }
    if (response?.status && response.status >= 500) {
      return 'Something went wrong on our end. Please try again shortly.';
    }
    return fallback;
  }

  if (err && typeof err === 'object' && 'request' in err) {
    return 'Unable to reach the server. Check your connection and try again.';
  }

  return fallback;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Attempts left before the backend's login lockout kicks in, from X-RateLimit-Remaining. */
  loginAttemptsRemaining: number | null;
  /** Seconds to wait when the login endpoint returns 429 — the login page renders its own countdown from this. */
  rateLimitRetryAfterSeconds: number | null;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      loginAttemptsRemaining: null,
      rateLimitRetryAfterSeconds: null,

      login: async (identifier, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await apiClient.post<LoginResponse>('/auth/login', {
            identifier,
            password,
          });
          const payload = decodeJwt(data.access_token);
          const user = payload ? userFromJwt(payload) : null;
          set({
            user,
            token: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            loginAttemptsRemaining: null,
            rateLimitRetryAfterSeconds: null,
          });
        } catch (err: unknown) {
          const response = (
            err as {
              response?: {
                status?: number;
                data?: { message?: string; retryAfterSeconds?: number };
                headers?: Record<string, string>;
              };
            }
          ).response;

          if (response?.status === 429) {
            set({
              isLoading: false,
              isAuthenticated: false,
              error: null,
              loginAttemptsRemaining: 0,
              rateLimitRetryAfterSeconds: response.data?.retryAfterSeconds ?? 60,
            });
            throw err;
          }

          const remainingHeader = response?.headers?.['x-ratelimit-remaining'];
          const remaining = remainingHeader !== undefined ? Number(remainingHeader) : NaN;
          const message = extractErrorMessage(err, 'Login failed. Check your credentials.');
          set({
            isLoading: false,
            error: message,
            isAuthenticated: false,
            loginAttemptsRemaining: Number.isFinite(remaining) ? remaining : null,
            rateLimitRetryAfterSeconds: null,
          });
          throw err;
        }
      },

      logout: async () => {
        try {
          await apiClient.post('/auth/logout');
        } catch {
          // Even if the server call fails (e.g. already-expired token), still
          // clear local state so the user isn't stuck "logged in" client-side.
        }
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          loginAttemptsRemaining: null,
          rateLimitRetryAfterSeconds: null,
        });
        setTimeout(() => { window.location.href = '/login'; }, 0);
      },

      refresh: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return;
        try {
          const { data } = await apiClient.post<LoginResponse>('/auth/refresh', {
            refreshToken,
          });
          const payload = decodeJwt(data.access_token);
          const user = payload ? userFromJwt(payload) : get().user;
          set({
            user,
            token: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
          });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
